// @flow strict
import React, { useEffect } from 'react'
import { graphql } from 'gatsby'
import _get from 'lodash.get'

import Global from '../Global'
import {
  ContentWrapper,
  SEOHead,
  type Location,
  PostListing,
} from '@mondough/public-web-components'

import HeroHeader from '../components/HeroHeader'
import Pagination from '../components/Pagination'

import {
  type allBlogPosts as ContentfulPosts,
  type allBlogPosts_allContentfulWebBlogPost_blogPosts_blogPost as ContentfulPost,
} from '../../__types__/allBlogPosts'
import setDimensionsFirePageview from '../utils/googleAnalytics.js'

type PageContext = {
  limit: number,
  skip: number,
  numPages: number,
  currentPage: number,
  basePath: string,
}

type Props = {
  data: ContentfulPosts,
  pageContext: PageContext,
  location: Location,
}

function extractBlogPosts(data: ContentfulPosts): Array<ContentfulPost> {
  const contentfulPosts = _get(data, 'allContentfulWebBlogPost.blogPosts', [])
  return contentfulPosts.map(node => node.blogPost)
}

const pageTitle = 'Monzo Blog'
const description =
  'The latest updates, stories, ideas and guides from the Monzo team.'

const IndexPage = ({ data, pageContext, location: { pathname } }: Props) => {
  const { currentPage, numPages, basePath } = pageContext
  const posts = extractBlogPosts(data)

  useEffect(() => {
    setDimensionsFirePageview(
      {
        dimension4: 'index',
      },
      pathname,
    )
  })

  return (
    <Global
      pageMetadata={<SEOHead pageTitle={pageTitle} description={description} />}
    >
      <HeroHeader title={pageTitle} strapline={description} />
      <ContentWrapper>
        <PostListing posts={posts} />
        <Pagination
          currentPage={currentPage}
          numPages={numPages}
          basePath={basePath}
        />
      </ContentWrapper>
    </Global>
  )
}

export const query = graphql`
  query allBlogPosts($skip: Int!, $limit: Int!) {
    allContentfulWebBlogPost(
      filter: { node_locale: { eq: "en-GB" } }
      sort: { fields: [startDate], order: DESC }
      limit: $limit
      skip: $skip
    ) {
      blogPosts: edges {
        blogPost: node {
          id
          title: blogTitle
          description {
            text: description
          }
          webSlug
          startDate
          createdAt
          category {
            categoryName
          }
          ogImage {
            file {
              url
            }
            description
          }
          ogImageUrl
        }
      }
    }
  }
`

export default IndexPage
